import React from 'react';
import { PropTypes } from 'prop-types';
// style
import styled, { css } from 'styled-components';
import { media } from 'utils/style';

const MainMenuButton = ({
  className,
  isCollapsed,
  text,
  icon,
  hideIconExpanded = false,
  ...props
}) => (
  <button className={className} {...props}>
    <div
      className={`buttonIcon ${
        hideIconExpanded && 'buttonIcon-hideIconExpanded'
      }`}
    >
      {icon}
    </div>
    <div
      className={`buttonText ${
        hideIconExpanded && 'buttonText-hideIconExpanded'
      }`}
    >
      <div className="verticalSeparator"></div>
      {text}
    </div>
  </button>
);

MainMenuButton.propTypes = {
  isCollapsed: PropTypes.bool,
  className: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.node,
};

const regularCSS = css`
  cursor: pointer;
  padding: 0;
  border: none;
  align-items: center;
  position: relative;
  display: flex;
  transition: margin-left 0.3s ease;
  width: 100%;
  background-color: var(--primary500);

  .buttonIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    flex-shrink: 0;
  }

  .buttonIcon-hideIconExpanded {
    display: none;
  }

  .buttonText {
    padding-right: 20px;
    height: 100%;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    border-radius: 0px 4px 4px 0px;
  }

  .buttonText-hideIconExpanded {
    margin-left: auto;
    margin-right: auto;
    padding: 0 16px !important;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      .buttonText {
        display: flex;
      }
    }
  }
  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
      cursor: default;
    `}
  ${(props) =>
    props.hidden &&
    css`
      opacity: 0;
      pointer-events: none;
      cursor: default;
    `}
`;

const variantDefaultRegularCSS = css`
  ${(props) =>
    props.variant === 'DEFAULT' &&
    css`
      height: 48px;
      .buttonIcon {
        width: 60px;
      }
      svg,
      svg path {
        fill: var(--grey200);
      }
      .buttonText {
        color: var(--grey200);
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          border-radius: 4px 0px 0px 4px;
          background-color: var(--primary-300-opacity10);
          svg,
          svg path {
            fill: var(--white) !important;
          }
          .buttonText {
            color: var(--white) !important;
          }
        }
      }
    `}
`;

const variantDefaultTabletCSS = css`
  ${(props) =>
    props.variant === 'DEFAULT' &&
    props.isCollapsed &&
    css`
      .buttonIcon {
        justify-content: start;
        padding-left: 4px;
      }
      .buttonText {
        display: none;
        padding-left: 16px;
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: var(--primary500) !important;
          .buttonText {
            background-color: var(--primary500) !important;
          }
        }
      }
    `}
`;

const variantTextHiddenRegularCSS = css`
  ${(props) =>
    props.variant === 'TEXT_HIDDEN' &&
    css`
      height: 80px;
      .buttonIcon {
        width: 80px;
      }
      .buttonText {
        display: none;
        color: var(--white);
      }
      svg,
      svg path {
        fill: var(--grey200);
      }
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          .buttonText {
            display: flex;
          }
          .verticalSeparator {
            margin-right: 20px;
            height: 32px;
            width: 1px;
            background-color: var(--primary200);
            opacity: 0.2;
          }
        }
      }
    `}
`;

const variantTextHiddenTabletCSS = css`
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      svg,
      svg path {
        fill: var(--white) !important;
      }
      .buttonText {
        display: flex;
        color: var(--white) !important;
      }
    }
  }
  ${(props) =>
    props.variant === 'TEXT_HIDDEN' &&
    props.isCollapsed &&
    css`
      .buttonText {
        display: none;
        padding-left: 16px;
        background-color: var(--primary500) !important;
      }
      .verticalSeparator {
        display: none;
      }
    `}
`;

const variantNewRegularCSS = css`
  ${(props) =>
    props.variant === 'NEW' &&
    css`
      border-radius: 4px;
      height: 40px;
      width: 230px;
      background: var(--primary-btn-main-menu);

      .buttonIcon {
        width: 40px;
        margin-left: 30px;
        transition: margin-left 0.3s ease;
      }
      .buttonText {
        padding-left: 8px;
        font-weight: 500;
        color: var(--white);
      }
      svg,
      svg path {
        fill: var(--white);
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background-color: var(--primary300);

          .buttonText {
            background-color: var(--primary300);
          }
        }
      }
    `}
  ${(props) =>
    props.variant === 'NEW' &&
    props.isCollapsed &&
    css`
      width: auto;
      .buttonIcon {
        margin-left: 0;
      }
      .buttonIcon-hideIconExpanded {
        display: flex;
      }
      .buttonText-hideIconExpanded {
        /* display: none; */
        border-radius: 4px;
      }
    `}
`;

const variantNewTabletCSS = css`
  ${(props) =>
    props.variant === 'NEW' &&
    props.isCollapsed &&
    css`
      width: auto;
      .buttonIcon {
        margin-left: 0;
      }
      .buttonText {
        display: none;
        border-radius: 4px;
      }
    `}
`;

export default styled(MainMenuButton)`
  ${regularCSS};
  ${variantDefaultRegularCSS};
  ${media.lessThan('huge')`${variantDefaultTabletCSS}`};
  ${variantTextHiddenRegularCSS};
  ${media.lessThan('huge')`${variantTextHiddenTabletCSS}`};
  ${variantNewRegularCSS};
  ${media.lessThan('huge')`${variantNewTabletCSS}`};
  ${variantDefaultRegularCSS};
`;
